import React, { useState, useEffect } from "react";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import { X, Users, Calendar as CalendarIcon, MapPin, Video } from "lucide-react";

export default function AssistanceModal({ taskId, task, onClose, onSuccess }) {
    const [users, setUsers] = useState([]);
    const [toUserId, setToUserId] = useState("");
    const [message, setMessage] = useState("");
    const [withMeeting, setWithMeeting] = useState(false);
    const [meetingType, setMeetingType] = useState("meeting_online");
    const [meetingStart, setMeetingStart] = useState("");
    const [meetingEnd, setMeetingEnd] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        API.get("/users").then(r => setUsers(r.data)).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!toUserId) { toast.error("Alege o persoană"); return; }
        if (!message.trim()) { toast.error("Scrie un mesaj"); return; }
        if (withMeeting && !meetingStart) { toast.error("Setează data și ora meetingului"); return; }
        setSubmitting(true);
        try {
            await API.post(`/tasks/${taskId}/assistance`, {
                task_id: taskId,
                to_user_id: toUserId,
                message: message.trim(),
                with_meeting: withMeeting,
                meeting_start: withMeeting ? new Date(meetingStart).toISOString() : null,
                meeting_end: withMeeting && meetingEnd ? new Date(meetingEnd).toISOString() : (withMeeting ? new Date(meetingStart).toISOString() : null),
                meeting_type: meetingType
            });
            toast.success("Cerere de asistență trimisă");
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white dark:bg-[#151A21] rounded-lg shadow-2xl w-full max-w-lg z-10 border border-[#E4E4E7] dark:border-[#252B33]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E4E7] dark:border-[#252B33]">
                    <div className="flex items-center gap-2">
                        <Users size={18} className="text-[#002FA7] dark:text-[#4F7CFF]" />
                        <h2 className="font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Solicită asistență</h2>
                    </div>
                    <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827] dark:hover:text-white"><X size={18} /></button>
                </div>

                <form onSubmit={submit} className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    {task?.title && (
                        <div className="text-xs text-[#9CA3AF] bg-[#F8F9FA] dark:bg-[#0B0D10] rounded px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33]">
                            Pentru task: <span className="font-semibold text-[#111827] dark:text-[#F3F4F6]">{task.title}</span>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#B5BAC1] uppercase tracking-wide mb-1.5">Persoană *</label>
                        <select value={toUserId} onChange={e => setToUserId(e.target.value)} required
                            data-testid="assistance-to-user"
                            className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm bg-white dark:bg-[#1C222A] text-[#111827] dark:text-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                            <option value="">— Alege —</option>
                            {users.filter(u => u.status !== "disabled").map(u => (
                                <option key={u.id} value={u.id}>{u.name} {u.email ? `(${u.email})` : ""}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#B5BAC1] uppercase tracking-wide mb-1.5">Mesaj *</label>
                        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} required
                            data-testid="assistance-message"
                            placeholder="Descrie pe scurt în ce ai nevoie de ajutor..."
                            className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm bg-white dark:bg-[#1C222A] text-[#111827] dark:text-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-[#002FA7] placeholder:text-[#9CA3AF] dark:placeholder:text-[#4B5563]" />
                    </div>

                    <div className="border-t border-[#E4E4E7] dark:border-[#252B33] pt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={withMeeting} onChange={e => setWithMeeting(e.target.checked)}
                                data-testid="assistance-with-meeting"
                                className="w-4 h-4 accent-[#002FA7]" />
                            <span className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6]">Propun un meeting</span>
                        </label>
                        <p className="text-xs text-[#9CA3AF] mt-1 ml-6">Dacă persoana acceptă, se adaugă automat în calendarul ambilor.</p>
                    </div>

                    {withMeeting && (
                        <div className="space-y-3 pl-6 border-l-2 border-[#002FA7]/30">
                            <div className="grid grid-cols-2 gap-2">
                                <button type="button" onClick={() => setMeetingType("meeting_online")}
                                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${meetingType === "meeting_online" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white dark:bg-[#1C222A] text-[#4B5563] dark:text-[#B5BAC1] border-[#E4E4E7] dark:border-[#252B33]"}`}>
                                    <Video size={13} /> Online
                                </button>
                                <button type="button" onClick={() => setMeetingType("meeting_physical")}
                                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${meetingType === "meeting_physical" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white dark:bg-[#1C222A] text-[#4B5563] dark:text-[#B5BAC1] border-[#E4E4E7] dark:border-[#252B33]"}`}>
                                    <MapPin size={13} /> Fizic
                                </button>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#B5BAC1] uppercase tracking-wide mb-1.5">Început *</label>
                                <input type="datetime-local" value={meetingStart} onChange={e => setMeetingStart(e.target.value)}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm bg-white dark:bg-[#1C222A] text-[#111827] dark:text-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#B5BAC1] uppercase tracking-wide mb-1.5">Sfârșit</label>
                                <input type="datetime-local" value={meetingEnd} onChange={e => setMeetingEnd(e.target.value)}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm bg-white dark:bg-[#1C222A] text-[#111827] dark:text-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose}
                            className="flex-1 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm text-[#4B5563] dark:text-[#B5BAC1] hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A]">Anulează</button>
                        <button type="submit" disabled={submitting}
                            data-testid="assistance-submit"
                            className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                            {submitting ? "Se trimite..." : "Trimite cererea"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
